import { CheckIcon, CopyIcon } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { toast } from "sonner"
import { TooltipButton } from "~/components/ui/button"
import {
	InputGroup,
	InputGroupAddon,
	InputGroupInput
} from "~/components/ui/input-group"

export function CopyButton({
	value,
	label,
	onCopy,
	size = "icon-sm"
}: {
	value: string
	label: string
	onCopy?: () => Promise<void>
	size?: "icon-xs" | "icon-sm"
}) {
	const [copied, setCopied] = useState(false)
	const [copying, setCopying] = useState(false)
	const reset = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
	const requestId = useRef(0)
	useEffect(() => {
		requestId.current++
		setCopied(false)
		setCopying(false)
		return () => {
			requestId.current++
			clearTimeout(reset.current)
		}
	}, [value])
	return (
		<TooltipButton
			type="button"
			variant="ghost"
			size={size}
			pending={copying}
			icon={copied ? <CheckIcon /> : <CopyIcon />}
			tooltip={copied ? "Copied to clipboard" : `Copy ${label.toLowerCase()}`}
			onClick={async () => {
				const request = requestId.current
				setCopying(true)
				try {
					if (onCopy) await onCopy()
					else await navigator.clipboard.writeText(value)
					if (request !== requestId.current) return
					setCopied(true)
					clearTimeout(reset.current)
					reset.current = setTimeout(() => setCopied(false), 2000)
				} catch {
					if (request === requestId.current)
						toast.error("Could not copy or share this value")
				} finally {
					if (request === requestId.current) setCopying(false)
				}
			}}
		/>
	)
}

export function CopyableField({
	value,
	label,
	onCopy,
	onChange
}: {
	value: string
	label: string
	onCopy?: () => Promise<void>
	onChange?: (value: string) => void
}) {
	return (
		<InputGroup>
			<InputGroupInput
				value={value}
				readOnly={!onChange}
				onChange={(event) => onChange?.(event.target.value)}
				aria-label={label}
				className="font-mono"
				onClick={(event) => event.currentTarget.select()}
			/>
			<InputGroupAddon align="inline-end">
				<CopyButton value={value} label={label} onCopy={onCopy} />
			</InputGroupAddon>
		</InputGroup>
	)
}
